import { useCallback, useState } from "react";
import { useClient } from "urql";
import { showErrorNotification } from "../shared/Notifications";
import { ExportPriorityEntriesQuery } from "../features/priority-entry/priorityEntry.queries";
import { downloadCSV } from "../utils/File.utils";
const exportColumns = [
    { key: "patientFirstName", header: "Patient First Name" },
    { key: "patientLastName", header: "Patient Last Name" },
    { key: "dateOfBirth", header: "Date of Birth" },
    { key: "city", header: "City" },
    { key: "state", header: "State" },
    { key: "postalCode", header: "Zip Code" },
    { key: "caregiverFirstName", header: "Caregiver First Name" },
    { key: "caregiverLastName", header: "Caregiver Last Name" },
    { key: "email", header: "Email" },
    { key: "phoneNumber", header: "Phone Number" },
    { key: "therapyCategory", header: "Therapy Category" },
    { key: "status", header: "Status" },
    { key: "inquiredAt", header: "Inquired At" }
];
export const useExportPriorityEntries = () => {
    const client = useClient();
    const [isExporting, setIsExporting] = useState(false);
    const exportPriorityEntries = useCallback(async (activeView) => {
        if (isExporting)
            return;
        if (!activeView?.priorityViewId) {
            showErrorNotification("Please select a view to export.");
            return;
        }
        setIsExporting(true);
        try {
            const { data, error } = await client
                .query(ExportPriorityEntriesQuery, {
                priorityViewId: activeView.priorityViewId
            })
                .toPromise();
            if (error || !data?.priorityEntries?.exportPriorityEntries) {
                showErrorNotification("Failed to export custom view.");
                return;
            }
            const entries = data.priorityEntries
                .exportPriorityEntries;
            if (entries.length === 0) {
                showErrorNotification("No entries found for this view.");
                return;
            }
            const rows = entries.map((entry) => ({
                patientFirstName: entry.patient?.firstName ?? "",
                patientLastName: entry.patient?.lastName ?? "",
                dateOfBirth: entry.patient?.dateOfBirth ?? "",
                city: entry.patient?.postalCodeInfo?.city ?? "",
                state: entry.patient?.postalCodeInfo?.stateId ?? "",
                postalCode: entry.patient?.postalCodeInfo?.postalCode ?? "",
                caregiverFirstName: entry.caregiver?.firstName ?? "",
                caregiverLastName: entry.caregiver?.lastName ?? "",
                email: entry.caregiver?.email ?? "",
                phoneNumber: entry.caregiver?.phoneNumber ?? "",
                therapyCategory: entry.therapyCategory?.name ?? "",
                status: entry.priorityAttribute?.priorityStatus?.name ?? "",
                inquiredAt: entry.priorityAttribute?.inquiredAt ?? ""
            }));
            const today = new Date().toISOString().slice(0, 10);
            const safeName = (activeView.name ?? "export").replace(/\s+/g, "_");
            downloadCSV(rows, exportColumns, `${safeName}_${today}.csv`);
        }
        catch {
            showErrorNotification("Failed to export priority entries.");
        }
        finally {
            setIsExporting(false);
        }
    }, [client, isExporting]);
    return { exportPriorityEntries, isExporting };
};
